import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaDownload,
  FaEye,
  FaHeart,
  FaRegHeart,
  FaFilePdf,
  FaFileWord,
  FaFilePowerpoint,
  FaFileAlt,
  FaFlag,
  FaUser,
  FaCalendarAlt,
  FaStar,
  FaStarHalfAlt,
  FaRegStar,
} from "react-icons/fa";
import { formatDistanceToNow } from "date-fns";
import { useSelector } from "react-redux";
import ReportModal from "../common/ReportModal";
import { buildApiUrl } from "../../utils/apiConfig";

const getAvatarDataUrl = (name) => {
  const initials =
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2) || "U";
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Crect width='40' height='40' fill='%238b5cf6'/%3E%3Ctext x='20' y='26' font-size='16' text-anchor='middle' fill='white' font-family='Arial'%3E${initials}%3C/text%3E%3C/svg%3E`;
};

const getFileIcon = (type) => {
  const fileType = type?.toLowerCase() || "";
  if (fileType.includes("pdf")) return <FaFilePdf size={40} color="#ef4444" />;
  if (fileType.includes("doc")) return <FaFileWord size={40} color="#3b82f6" />;
  if (fileType.includes("ppt"))
    return <FaFilePowerpoint size={40} color="#f59e0b" />;
  return <FaFileAlt size={40} color="#8b5cf6" />;
};

// Helper to get rating stars
const getRatingStars = (rating) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  for (let i = 0; i < fullStars; i++) {
    stars.push(<FaStar key={i} size={12} color="#f59e0b" />);
  }
  if (hasHalfStar) {
    stars.push(<FaStarHalfAlt key="half" size={12} color="#f59e0b" />);
  }
  while (stars.length < 5) {
    stars.push(<FaRegStar key={stars.length} size={12} color="#cbd5e1" />);
  }
  return stars;
};

const NoteCard = ({ note, onLike, isDarkMode }) => {
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const isLiked = user && note.likes?.includes(user.id || user._id);
  const [showReportModal, setShowReportModal] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Get profile image URL
  const getProfileImageUrl = () => {
    if (!note.uploadedBy?.profilePicture || imageError) {
      return getAvatarDataUrl(note.uploadedBy?.name);
    }
    if (note.uploadedBy?.profilePicture?.startsWith("http")) {
      return note.uploadedBy.profilePicture;
    }
    return buildApiUrl(note.uploadedBy?.profilePicture);
  };

  // Card background based on dark mode
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";

  return (
    <>
      <motion.div
        whileHover={{ y: -8, scale: 1.02 }}
        transition={{ type: "spring", stiffness: 400, damping: 17 }}
        style={{
          background: getCardBackground(),
          backdropFilter: "blur(20px)",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: isDarkMode
            ? "0 25px 50px -12px rgba(0,0,0,0.5)"
            : "0 25px 50px -12px rgba(0,0,0,0.25)",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          border: `1px solid ${getBorderColor()}`,
          transition: "all 0.3s ease",
        }}
      >
        {/* Card Header with Icon and Badge */}
        <Link to={`/notes/${note._id}`} style={{ textDecoration: "none" }}>
          <div
            style={{
              height: "120px",
              background: `linear-gradient(135deg, ${isDarkMode ? "#1e1b4b" : "#f8fafc"}, ${isDarkMode ? "#2e1065" : "#f1f5f9"})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              borderBottom: `1px solid ${getBorderColor()}`,
            }}
          >
            <div
              style={{
                width: "70px",
                height: "70px",
                background: isDarkMode
                  ? "rgba(139,92,246,0.1)"
                  : "rgba(139,92,246,0.05)",
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {getFileIcon(note.fileType)}
            </div>
            <span
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                padding: "0.25rem 0.75rem",
                borderRadius: "20px",
                color: "white",
                fontSize: "0.7rem",
                fontWeight: "600",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {note.fileType || "PDF"}
            </span>
            {/* Rating Badge */}
            {note.rating > 0 && (
              <div
                style={{
                  position: "absolute",
                  bottom: "12px",
                  right: "12px",
                  background: isDarkMode
                    ? "rgba(0,0,0,0.6)"
                    : "rgba(255,255,255,0.9)",
                  backdropFilter: "blur(10px)",
                  padding: "0.25rem 0.6rem",
                  borderRadius: "20px",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.3rem",
                }}
              >
                {getRatingStars(note.rating)}
                <span
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: "600",
                    color: "#f59e0b",
                  }}
                >
                  {note.rating}
                </span>
              </div>
            )}
          </div>
        </Link>

        <div style={{ padding: "1.25rem", flex: 1 }}>
          {/* Title and Report Button */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "0.5rem",
              marginBottom: "0.75rem",
            }}
          >
            <Link
              to={`/notes/${note._id}`}
              style={{ textDecoration: "none", flex: 1 }}
            >
              <h3
                style={{
                  fontSize: "1rem",
                  fontWeight: "700",
                  color: getTextColor(),
                  marginBottom: "0",
                  lineHeight: "1.4",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {note.title}
              </h3>
            </Link>
            {isAuthenticated && user?._id !== note.uploadedBy?._id && (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowReportModal(true)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: getTextSecondary(),
                  padding: "0.3rem",
                  borderRadius: "8px",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#ef4444";
                  e.currentTarget.style.background = "rgba(239,68,68,0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = getTextSecondary();
                  e.currentTarget.style.background = "transparent";
                }}
                title="Report this note"
              >
                <FaFlag size={14} />
              </motion.button>
            )}
          </div>

          {/* Tags */}
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              marginBottom: "1rem",
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                padding: "0.2rem 0.6rem",
                borderRadius: "20px",
                fontSize: "0.7rem",
                color: "white",
                fontWeight: "600",
              }}
            >
              {note.subjectCode}
            </span>
            <span
              style={{
                background: isDarkMode ? "rgba(139,92,246,0.1)" : "#f1f5f9",
                padding: "0.2rem 0.6rem",
                borderRadius: "20px",
                fontSize: "0.7rem",
                color: getTextSecondary(),
                fontWeight: "500",
              }}
            >
              Sem {note.semester}
            </span>
            {note.branch && (
              <span
                style={{
                  background: isDarkMode ? "rgba(139,92,246,0.1)" : "#f1f5f9",
                  padding: "0.2rem 0.6rem",
                  borderRadius: "20px",
                  fontSize: "0.7rem",
                  color: getTextSecondary(),
                  fontWeight: "500",
                }}
              >
                {note.branch}
              </span>
            )}
          </div>

          {/* Description */}
          <p
            style={{
              color: getTextSecondary(),
              fontSize: "0.85rem",
              marginBottom: "1rem",
              lineHeight: "1.5",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {note.description}
          </p>

          {/* Author Info */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "1rem",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
              }}
            >
              <img
                src={getProfileImageUrl()}
                alt={note.uploadedBy?.name || "User"}
                onError={() => setImageError(true)}
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "2px solid #8b5cf6",
                }}
              />
              <div>
                <div
                  style={{
                    color: getTextColor(),
                    fontSize: "0.8rem",
                    fontWeight: "600",
                  }}
                >
                  {note.uploadedBy?.name || "Unknown User"}
                </div>
                <div
                  style={{
                    color: getTextSecondary(),
                    fontSize: "0.65rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.3rem",
                  }}
                >
                  <FaCalendarAlt size={10} />
                  {note.createdAt
                    ? formatDistanceToNow(new Date(note.createdAt), {
                        addSuffix: true,
                      })
                    : "Recently"}
                </div>
              </div>
            </div>
          </div>

          {/* Stats and Like Button */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderTop: `1px solid ${getBorderColor()}`,
              paddingTop: "0.75rem",
              marginTop: "0.25rem",
            }}
          >
            <div style={{ display: "flex", gap: "1rem" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.3rem",
                  color: getTextSecondary(),
                  fontSize: "0.8rem",
                }}
              >
                <FaEye size={14} /> {note.views || 0}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.3rem",
                  color: getTextSecondary(),
                  fontSize: "0.8rem",
                }}
              >
                <FaDownload size={14} /> {note.downloads || 0}
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onLike(note._id);
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.3rem",
                color: isLiked ? "#ef4444" : getTextSecondary(),
                fontSize: "0.8rem",
                transition: "all 0.2s",
                padding: "0.3rem 0.6rem",
                borderRadius: "20px",
              }}
              onMouseEnter={(e) => {
                if (!isLiked) {
                  e.currentTarget.style.color = "#ef4444";
                  e.currentTarget.style.background = "rgba(239,68,68,0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isLiked) {
                  e.currentTarget.style.color = getTextSecondary();
                  e.currentTarget.style.background = "transparent";
                }
              }}
            >
              {isLiked ? <FaHeart size={14} /> : <FaRegHeart size={14} />}
              <span>{note.likes?.length || 0}</span>
            </motion.button>
          </div>
        </div>
      </motion.div>

      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        noteId={note._id}
        noteTitle={note.title}
      />
    </>
  );
};

export default NoteCard;
